import { Equipment } from './equipment.model';
import { Material } from './material.model';
import { Document } from './document.model';
import { Crew } from './crew.model';
import { Organization } from './organization.model';
import { Project } from './project.model';

export enum TaskStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  COMPLETE = 'COMPLETE',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  INTERNAL_APPROVAL = 'INTERNAL_APPROVAL',
  CLIENT_APPROVAL = 'CLIENT_APPROVAL',
  DELEGATION_CANCELED = 'DELEGATION_CANCELED',
}

export enum TaskDisplayValues {
  COMPLETE = 'COMPLETE',
  CLIENT_APPROVAL = 'CLIENT_APPROVAL',
  INTERNAL_APPROVAL = 'INTERNAL_APPROVAL',
  INACTIVE = 'INACTIVE',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  DELEGATION_CANCELED = 'DELEGATION_CANCELED',
  UPCOMING = 'UPCOMING',
  BEHIND_SCHEDULE = 'BEHIND_SCHEDULE',
  UNASSIGNED = 'UNASSIGNED',
  ASSIGNED_TO_CREW = 'ASSIGNED_TO_CREW',
  ASSIGNED_TO_SUBCONTRACTOR = 'ASSIGNED_TO_SUBCONTRACTOR',
  IN_NEGOTIATIONS = 'IN_NEGOTIATIONS',
}

export interface MaterialCreateInput {
  materialId?: string;
  name: string;
  supplier: string;
  amount: number;
  amountType: string;
  amountSystem?: string;
  costPerUnit: number;
  pricePerUnit?: number;
  chargeToClient?: boolean;
  time?: string | Date;
}

export interface EquipmentCreateInput {
  equipmentId?: string;
  name: string;
  supplierOwner: string;
  hoursUsed: number;
  amount: number;
  costPerHour: number;
  pricePerHour?: number;
  chargeToClient?: boolean;
  time?: string | Date;
}

export interface MaterialUpdateInput {
  materialId: string;
  name?: string;
  supplier?: string;
  amount?: number;
  amountType?: string;
  amountSystem?: string;
  costPerUnit?: number;
  pricePerUnit?: number;
  chargeToClient?: boolean;
}

export interface EquipmentUpdateInput {
  equipmentId: string;
  name?: string;
  supplierOwner?: string;
  hoursUsed?: number;
  amount?: number;
  costPerHour?: number;
  pricePerHour?: number;
  chargeToClient?: boolean;
}

export interface MaterialEquipmentOperationInput {
  materialsToCreate?: MaterialCreateInput[];
  materialsToUpdate?: MaterialUpdateInput[];
  materialsToDelete?: string[];
  equipmentsToCreate?: EquipmentCreateInput[];
  equipmentsToUpdate?: EquipmentUpdateInput[];
  equipmentsToDelete?: string[];
}

// State management interface for tracking operations in UI
export interface MaterialEquipmentOperationState {
  materialsToCreate: MaterialCreateInput[];
  materialsToUpdate: MaterialUpdateInput[];
  materialsToDelete: string[];
  equipmentsToCreate: EquipmentCreateInput[];
  equipmentsToUpdate: EquipmentUpdateInput[];
  equipmentsToDelete: string[];
}

// Helper interface for material/equipment with operation status
export interface MaterialWithOperation extends Material {
  operationType?: 'create' | 'update' | 'delete' | 'existing';
}

export interface EquipmentWithOperation extends Equipment {
  operationType?: 'create' | 'update' | 'delete' | 'existing';
}

export interface Task {
  taskId?: string;
  name: string;
  description: string;
  taskType: string;
  otherType: string | null;
  manHours: number;
  startTime: string | Date; // Pattern B: Date-only field (despite name)
  endTime: string | Date; // Pattern B: Date-only field (despite name)
  createdAt?: string | Date; // Pattern A: Auto-generated timestamp
  status?: TaskStatus;
  timeConstraint: number;
  typeOfTimeConstraint: string;
  documents: Document[];
  superintendentId?: string | null;
  projectId?: string;
  /**
   * Only the fields the calling query asked for — queries pick a handful
   * (name, siteImage) rather than the whole project.
   */
  project?: Partial<Project>;
  parentTaskId?: string | null | undefined;
  crewIds?: string[];
  delegateOrganization?: Organization;
  organizationId?: string | null;
  crews?: Crew[] | undefined;
  presedentTaskId?: string[];
  presedentEventId?: string[];
  equipmentsIds?: Equipment[];
  materialsIds?: Material[];
  equipments?: Equipment[] | undefined;
  materials?: Material[] | undefined;
  deleteDocuments?: string[] | null;
  sortOrder?: number;
  delegateOrganizationId?: string | null;
  isDelegated?: boolean;
  delegateTaskId?: string | null;
  originalTaskId?: string | null;
  originalProjectId?: string | null;
  originalOrganizationId?: string | null;
  originalOrganization?: Organization;
  unassigned?: boolean;
  children?: Task[];
  rescheduledTasksIds?: string[];
  note?: string | null | undefined;
  budget?: number | null;
}

export interface TaskCreateInput extends Omit<Task, 'materials' | 'equipments' | 'status'> {
  materials?: MaterialCreateInput[];
  equipments?: EquipmentCreateInput[];
  subtasks?: {
    add: string[];
    remove: string[];
  };
}

export interface TaskUpdateInput {
  taskId: string;
  name?: string;
  // Nullable, not just optional: omitting the field leaves the column alone, so an explicit
  // null is the only way to clear a description the user emptied.
  description?: string | null;
  taskType?: string;
  otherType?: string | null;
  manHours?: number;
  startTime?: string | Date;
  endTime?: string | Date;
  timeConstraint?: number;
  typeOfTimeConstraint?: string;
  superintendentId?: string | null;
  projectId?: string;
  parentTaskId?: string | null;
  crewIds?: string[];
  organizationId?: string | null;
  delegateOrganizationId?: string | null;
  presedentTaskId?: string[];
  presedentEventId?: string[];
  note?: string | null;
  budget?: number | null;
  materialEquipmentOperations?: MaterialEquipmentOperationInput;
  subtasks?: {
    add: string[];
    remove: string[];
  };
}

export interface getTaskRequest {
  getTasks: Task[];
}

export interface createOrUpdateTaskRequest {
  createTask?: Task;
  updateTask?: Task;
}

export interface deleteTaskRequest {
  deleteTask: {
    affected: number;
  };
}

/**
 * Sprint 12, story-054/055 (U11/N2). Shared data contract with backend's `PendingApprovalDto` —
 * one query, one shape, two clients (see docs/ux/story-055-design.md's "Consumed data contract").
 */
export interface PendingApproval {
  taskId: string;
  taskName: string;
  projectId: string;
  projectName?: string | null;
  kind: 'INTERNAL_APPROVAL' | 'CLIENT_APPROVAL';
  /** What approving sets the task to: COMPLETE, or CLIENT_APPROVAL when the work goes on to a client. */
  approveOutcome: 'COMPLETE' | 'CLIENT_APPROVAL';
  requestedByName?: string | null;
  enteredApprovalAt: string;
}
